import React, { useRef, useState, useEffect } from "react";
import "../../styles/commonStyles/customDateInput.css";
import EventIcon from "@mui/icons-material/Event";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, getMonth, getYear, isValid, parse, parseISO } from "date-fns";
import { ChevronLeftOutlined, ChevronRightOutlined } from "@mui/icons-material";
import { Select, MenuItem } from "@mui/material";

const CustomDateInput = ({
  name,
  value,
  handleInputChange,
  handleCalendarChange,
  datePickerRef,
  selectedDate,
  maxDate: maxDateProp,
}) => {
  const maxDate = maxDateProp !== undefined ? maxDateProp : new Date();
  const currentYear = new Date().getFullYear();
  const topYear = maxDate ? getYear(maxDate) : currentYear + 5;
  const years = Array.from(
    { length: topYear - 1900 + 1 },
    (_, i) => topYear - i,
  );
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const yearSelectRef = useRef(null);
  const [yearMenuOpen, setYearMenuOpen] = useState(false);

  useEffect(() => {
  if (!yearMenuOpen) return;

  const handleWheelOutside = (event) => {
    const menuPaper = document.querySelector(".MuiMenu-paper");

    if (!menuPaper) return;

    if (menuPaper.contains(event.target)) {
      return;
    }

    setYearMenuOpen(false);
  };

  document.addEventListener("wheel", handleWheelOutside, true);

  return () => {
    document.removeEventListener(
      "wheel",
      handleWheelOutside,
      true
    );
  };
}, [yearMenuOpen]);

  return (
    <>
      <DatePicker
        ref={datePickerRef}
        selected={selectedDate}
        onChange={(date) => {
          handleCalendarChange(date); // call the original handler
        }}
        dateFormat="MM/dd/yyyy"
        minDate={new Date("1900-01-01")}
        maxDate={maxDate}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        customInput={
          <div className="inputWrapper">
            <input
              type="text"
              className="inputDatepick"
              value={value}
              onChange={handleInputChange}
              placeholder="MM/DD/YYYY"
              maxLength={10}
              name={name}
            />

            <EventIcon className="calendarIcon" />
          </div>
        }
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div
            style={{
              marginBottom: "10px",
              display: "flex",
              justifyContent: "space-between",
              minWidth: "250px",
            }}
          >
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              style={{ border: "none", background: "none" }}
            >
              <ChevronLeftOutlined
                sx={{
                  fontSize: "24px",
                  color: "#000",
                  cursor: "pointer",
                  visibility: prevMonthButtonDisabled ? "hidden" : "visible",
                }}
              />
            </button>
            <Select
              ref={yearSelectRef}
              variant="standard"
              sx={{ width: "70px" }}
              className="react-datepicker__current-year"
              value={getYear(date)}
              open={yearMenuOpen}
              onOpen={() => setYearMenuOpen(true)}
              onClose={() => setYearMenuOpen(false)}
              onChange={({ target: { value } }) => {
                changeYear(value);
                setYearMenuOpen(false);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: {
                      xs: "300px",
                      sm: "300px",
                      md: "300px",
                      lg: "300px",
                    },
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "grey",
                      borderRadius: "25px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                    },
                  },
                },
                // Optional: Keep the menu from auto-closing when used inside popper
                disableScrollLock: true,
                sx: {
                  zIndex: 99999,
                },
              }}
            >
              {years.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    fontSize: "14px",
                    minHeight: "unset",
                    fontFamily: "proxima-regular",
                    padding: "6px 6px",
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Select
              sx={{ width: "100px" }}
              className="react-datepicker__current-month"
              variant="standard"
              value={months[getMonth(date)]}
              onChange={({ target: { value } }) =>
                changeMonth(months.indexOf(value))
              }
              onMouseDown={(e) => e.stopPropagation()}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: {
                      xs: "300px",
                      sm: "300px",
                      md: "300px",
                      lg: "300px",
                    },
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "grey",
                      borderRadius: "25px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                    },
                  },
                },
                // Optional: Keep the menu from auto-closing when used inside popper
                disableScrollLock: true,
                sx: {
                  zIndex: 99999,
                },
              }}
            >
              {months.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    fontSize: "14px",
                    minHeight: "unset",
                    fontFamily: "proxima-regular",
                    padding: "6px 6px",
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              style={{ border: "none", background: "none" }}
            >
              <ChevronRightOutlined
                sx={{
                  fontSize: "24px",
                  color: "#000",
                  cursor: "pointer",
                  visibility: nextMonthButtonDisabled ? "hidden" : "visible",
                }}
              />
            </button>
          </div>
        )}
      />
    </>
  );
};

export default CustomDateInput;
